import { useState } from "react";

export type StudioButtonVariant =
	| "primary"
	| "secondary"
	| "ghost"
	| "quiet"
	| "dark"
	| "onDark";

export type StudioButtonSize = "xs" | "sm" | "md" | "lg";

export interface StudioButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: StudioButtonVariant;
	size?: StudioButtonSize;
	icon?: React.ComponentType<{ size?: number; strokeWidth?: number }>;
	iconRight?: React.ComponentType<{ size?: number; strokeWidth?: number }>;
}

const sizeMap: Record<
	StudioButtonSize,
	{ height: number; px: number; fontSize: number }
> = {
	xs: { height: 30, px: 12, fontSize: 12 },
	sm: { height: 36, px: 14, fontSize: 13 },
	md: { height: 44, px: 18, fontSize: 14 },
	lg: { height: 52, px: 24, fontSize: 15 },
};

const variantStyles: Record<
	StudioButtonVariant,
	{
		bg: string;
		color: string;
		border: string;
		shadow: string;
		hoverBg?: string;
		hoverShadow?: string;
		/** Shadow while disabled. Needed when the edge is part of the shadow. */
		disabledShadow?: string;
	}
> = {
	primary: {
		bg: "var(--color-dashboard-accent)",
		color: "var(--color-dashboard-on-fill)",
		border: "transparent",
		shadow:
			"rgba(255, 237, 213, 0.35) 0px 1px 0px inset, color-mix(in srgb, var(--color-dashboard-shadow-ink) 15%, transparent) 0px -1px 0px inset, color-mix(in srgb, var(--color-dashboard-accent) 35%, transparent) 0px 6px 18px -6px, color-mix(in srgb, var(--color-dashboard-accent) 30%, transparent) 0px 12px 32px -10px",
	},
	secondary: {
		bg: "var(--color-dashboard-paper)",
		color: "var(--color-dashboard-text)",
		border: "none",
		shadow: "var(--dashboard-shadow-secondary)",
		hoverBg: "var(--color-dashboard-surface)",
		hoverShadow: "var(--dashboard-shadow-secondary-hover)",
		disabledShadow: "var(--dashboard-shadow-secondary-disabled)",
	},
	ghost: {
		bg: "transparent",
		color: "var(--color-dashboard-muted-dark)",
		border: "transparent",
		shadow: "none",
	},
	quiet: {
		bg: "transparent",
		color: "var(--color-dashboard-muted-dark)",
		border: "1px solid var(--color-dashboard-border)",
		shadow: "none",
	},
	dark: {
		bg: "var(--color-dashboard-dark)",
		color: "var(--color-dashboard-on-fill)",
		border: "transparent",
		shadow: "0 2px 8px rgba(0,0,0,0.2)",
	},
	onDark: {
		bg: "color-mix(in srgb, var(--color-dashboard-on-fill) 12%, transparent)",
		color: "var(--color-dashboard-on-fill)",
		border:
			"1px solid color-mix(in srgb, var(--color-dashboard-on-fill) 20%, transparent)",
		shadow: "none",
	},
};

export function StudioButton({
	variant = "primary",
	size = "md",
	icon: Icon,
	iconRight: IconRight,
	children,
	style,
	onMouseEnter,
	onMouseLeave,
	...props
}: StudioButtonProps) {
	const { height, px, fontSize } = sizeMap[size];
	const v = variantStyles[variant];
	const [isHovered, setIsHovered] = useState(false);

	const hovered = isHovered && !props.disabled;
	const hoverTransform = hovered ? "translateY(-1px)" : "translateY(0)";

	return (
		<button
			type="button"
			style={{
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "center",
				gap: 8,
				height,
				paddingLeft: px,
				paddingRight: px,
				fontSize,
				fontFamily: "var(--font-body)",
				fontWeight: 500,
				color: v.color,
				background: hovered && v.hoverBg ? v.hoverBg : v.bg,
				border: v.border,
				borderRadius: 8,
				boxShadow: props.disabled
					? (v.disabledShadow ?? "none")
					: hovered && v.hoverShadow
						? v.hoverShadow
						: v.shadow,
				cursor: props.disabled ? "not-allowed" : "pointer",
				transition: "all 200ms cubic-bezier(0.22,1,0.36,1)",
				opacity: props.disabled ? 0.6 : 1,
				...style,
				transform: style?.transform
					? `${style.transform} ${hoverTransform}`
					: hoverTransform,
			}}
			{...props}
			onMouseEnter={(event) => {
				setIsHovered(true);
				onMouseEnter?.(event);
			}}
			onMouseLeave={(event) => {
				setIsHovered(false);
				onMouseLeave?.(event);
			}}
		>
			{/* Passed explicitly: the global CSS rule only reaches lucide icons. */}
			{Icon && <Icon size={height * 0.45} strokeWidth={1.75} />}
			{children}
			{IconRight && <IconRight size={height * 0.45} strokeWidth={1.75} />}
		</button>
	);
}
